/**
 * O passo "Configurar" do Facebook e do Instagram (29/09/2026), no formato do
 * motor de conversa: entrar com o Facebook, escolher a página, dar um nome.
 *
 * ── Por que o Instagram passa pela página ────────────────────────────────
 *
 * O servidor conecta pela página (`meta-oauth.ts`): o motor registra a página
 * e liga sozinho a conta Instagram profissional que estiver vinculada a ela. O
 * caminho próprio de Instagram do motor devolveria o cliente para o endereço
 * DO MOTOR, que a regra de produto não mostra. Então o cartão do Instagram
 * leva ao mesmo lugar, e a lista destaca as páginas que têm Instagram.
 *
 * ── Três momentos ─────────────────────────────────────────────────────────
 *
 *   1. antes: o botão que leva à Meta;
 *   2. na volta: "buscando as páginas";
 *   3. com a lista: escolher uma, nomear a caixa, conectar (com step-up).
 */

import { useState, type ReactNode } from 'react';
import type { PaginasDaMeta } from '@cliqcrm/contracts';
import { Botao, Campo, Chip } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { useApi } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import type { UsoDeStepUp } from '@/features/acesso/confirmar-identidade';
import { lembrarCanalDaMeta, type CanalDaMeta } from '../lib/retorno-da-meta';

export type EstadoDaMeta = null | 'buscando' | PaginasDaMeta;

export function ConectarPaginaDaMeta({
  canal,
  estado,
  stepUp,
  aoConectar,
  aoEscolherOutro,
}: {
  canal: CanalDaMeta;
  estado: EstadoDaMeta;
  stepUp: UsoDeStepUp;
  aoConectar: (canal: { inbox: string; nome: string }) => void;
  aoEscolherOutro: () => void;
}): ReactNode {
  const api = useApi();
  const [saindo, definirSaindo] = useState(false);
  const [conectando, definirConectando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const [escolhida, definirEscolhida] = useState<string | null>(null);
  const [nome, definirNome] = useState('');

  const semConexao = 'Sem conexão agora. Tente de novo em instantes.';

  const entrar = async () => {
    definirErro(null);
    definirSaindo(true);
    try {
      const { url } = await api!.autorizarMeta({});
      lembrarCanalDaMeta(canal);
      location.assign(url);
    } catch (falha) {
      definirSaindo(false);
      definirErro(
        recadoDeErro(falha, { generica: 'Não foi possível abrir a autorização da Meta.' }),
      );
    }
  };

  if (estado === null) {
    return (
      <div className="passos-canal__corpo">
        <p className="admin__apoio">
          {canal === 'instagram'
            ? 'O Instagram entra pela página do Facebook ligada a ele. A conta precisa ser ' +
              'profissional e estar vinculada a uma página que você administra.'
            : 'Entre com o Facebook de quem administra a página. Se ela tiver um Instagram ' +
              'profissional ligado, as mensagens dele chegam junto.'}{' '}
          Você vai para a Meta autorizar e volta para cá para escolher a página.
        </p>
        <div className="passos-canal__acoes">
          <Botao
            variante="primario"
            desabilitado={saindo || api === null}
            motivo={api === null ? semConexao : 'Abrindo a Meta…'}
            onClick={() => void entrar()}
          >
            {saindo ? 'Abrindo a Meta…' : 'Entrar com o Facebook'}
          </Botao>
          <Botao onClick={aoEscolherOutro}>Escolher outro canal</Botao>
        </div>
        {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
      </div>
    );
  }

  if (estado === 'buscando') {
    return (
      <div className="passos-canal__corpo">
        <p className="admin__apoio" role="status">
          Buscando as páginas da sua conta…
        </p>
      </div>
    );
  }

  const pagina = estado.paginas.find((p) => p.id === escolhida) ?? null;
  const nomeFinal = nome.trim();
  const motivoDaPagina = (p: PaginasDaMeta['paginas'][number]): string | null =>
    p.ja_conectada
      ? 'Já conectada'
      : canal === 'instagram' && !p.tem_instagram
        ? 'Sem Instagram ligado'
        : null;

  const conectar = async () => {
    if (!pagina) return;
    definirErro(null);
    definirConectando(true);
    try {
      await stepUp.comStepUp(async () => {
        const criado = await api!.conectarPaginaDaMeta({
          corpo: { autorizacao: estado.autorizacao, page_id: pagina.id, nome: nomeFinal },
        });
        aoConectar({ inbox: String(criado.inbox_id), nome: criado.nome });
      }, 'meta_page');
    } catch (falha) {
      definirErro(recadoDeErro(falha, { generica: 'Não foi possível conectar a página.' }));
    } finally {
      definirConectando(false);
    }
  };

  return (
    <div className="passos-canal__corpo">
      {estado.paginas.length === 0 ? (
        <p className="admin__apoio">
          Esta conta não administra nenhuma página. Entre com o Facebook de quem administra a página
          da sua empresa.
        </p>
      ) : (
        <fieldset className="paginas-meta">
          <legend className="atendentes-canal__legenda">Escolha a página</legend>
          <ul className="paginas-meta__lista">
            {estado.paginas.map((p) => {
              const motivo = motivoDaPagina(p);
              return (
                <li key={p.id}>
                  <label
                    className="paginas-meta__pagina"
                    data-desabilitada={motivo ? 'sim' : undefined}
                  >
                    <input
                      type="radio"
                      name="pagina-da-meta"
                      value={p.id}
                      checked={escolhida === p.id}
                      disabled={motivo !== null}
                      onChange={() => {
                        definirEscolhida(p.id);
                        definirNome(p.nome);
                      }}
                    />
                    <span className="paginas-meta__nome">{p.nome}</span>
                    {p.tem_instagram && <Chip>Instagram ligado</Chip>}
                    {motivo && <Chip>{motivo}</Chip>}
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      )}

      {pagina && (
        <Campo
          rotulo="Nome do canal"
          ajuda="É como ele aparece no Atendimento e na lista de canais."
          value={nome}
          maxLength={60}
          onChange={(e) => definirNome(e.target.value)}
        />
      )}

      <div className="passos-canal__acoes">
        <Botao
          variante="primario"
          desabilitado={!pagina || nomeFinal.length < 2 || conectando || api === null}
          motivo={
            api === null
              ? semConexao
              : !pagina
                ? 'Escolha uma página.'
                : nomeFinal.length < 2
                  ? 'Dê um nome ao canal.'
                  : 'Conectando…'
          }
          onClick={() => void conectar()}
        >
          {conectando ? 'Conectando…' : 'Conectar página'}
        </Botao>
        <Botao onClick={aoEscolherOutro}>Escolher outro canal</Botao>
      </div>
      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
    </div>
  );
}
